import React, { memo, useState } from 'react'
import { Outlet, Link, useLocation, Navigate } from 'react-router-dom'
import { useAuthStore } from '../../store/authStore'

interface AdminNavItem {
  label: string
  href: string
  icon: React.ReactNode
}

const ADMIN_NAV_ITEMS: AdminNavItem[] = [
  {
    label: 'Dashboard',
    href: '/dashboard',
    icon: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <rect x="3" y="3" width="7" height="7" />
        <rect x="14" y="3" width="7" height="7" />
        <rect x="14" y="14" width="7" height="7" />
        <rect x="3" y="14" width="7" height="7" />
      </svg>
    ),
  },
  {
    label: 'Products',
    href: '/dashboard/products',
    icon: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
      </svg>
    ),
  },
  {
    label: 'Orders',
    href: '/dashboard/orders',
    icon: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path d="M9 11l3 3L22 4" />
        <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
      </svg>
    ),
  },
  {
    label: 'Inventory',
    href: '/dashboard/inventory',
    icon: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <line x1="8" y1="6" x2="21" y2="6" />
        <line x1="8" y1="12" x2="21" y2="12" />
        <line x1="8" y1="18" x2="21" y2="18" />
        <line x1="3" y1="6" x2="3.01" y2="6" />
        <line x1="3" y1="12" x2="3.01" y2="12" />
        <line x1="3" y1="18" x2="3.01" y2="18" />
      </svg>
    ),
  },
  {
    label: 'Analytics',
    href: '/dashboard/analytics',
    icon: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <line x1="18" y1="20" x2="18" y2="10" />
        <line x1="12" y1="20" x2="12" y2="4" />
        <line x1="6" y1="20" x2="6" y2="14" />
      </svg>
    ),
  },
  {
    label: 'Users',
    href: '/dashboard/users',
    icon: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
  },
]

/**
 * AdminLayout — isolated shell for /dashboard/* routes.
 * Does NOT include the public Header or Footer.
 * Has its own sidebar navigation.
 */
export const AdminLayout = memo(function AdminLayout() {
  const { user, clearAuth, isAuthenticated } = useAuthStore()
  const location = useLocation()
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)

  // if (!isHydrated) return null


  if (!isAuthenticated || user?.role !== 'admin') {
    return <Navigate to="/" replace />
  }

  return (
    <div className="min-h-screen bg-[#F0F0ED] flex font-sans antialiased">

      {/* ── Sidebar ──────────────────────────────────────────────────────── */}
      <aside
        aria-label="Admin navigation"
        className={`
          fixed top-0 left-0 bottom-0 z-20
          bg-charcoal text-white
          flex flex-col
          transition-all duration-200
          ${sidebarCollapsed ? 'w-16' : 'w-60'}
        `}
      >
        {/* Sidebar header */}
        <div className={`flex items-center border-b border-white/10 h-16 shrink-0 ${sidebarCollapsed ? 'justify-center px-0' : 'px-5 gap-3'}`}>
          {!sidebarCollapsed && (
            <Link to="/" aria-label="Back to OneTech store" className="focus-visible:outline-none focus-visible:underline">
              <span className="font-display font-bold text-[1.1rem] text-white">
                One<span className="text-royal-orange">Tech</span>
              </span>
            </Link>
          )}
          <button
            onClick={() => setSidebarCollapsed((v) => !v)}
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className="
              w-7 h-7 flex items-center justify-center rounded-sm
              text-white/60 hover:text-white
              ml-auto focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-royal-orange
            "
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              {sidebarCollapsed
                ? <><line x1="3" y1="12" x2="21" y2="12" /><polyline points="9 6 3 12 9 18" /></>
                : <><line x1="21" y1="12" x2="3" y2="12" /><polyline points="15 6 21 12 15 18" /></>
              }
            </svg>
          </button>
        </div>

        {/* Nav items */}
        <nav className="flex-1 overflow-y-auto py-4">
          <ul role="list" className="flex flex-col gap-1 px-2">
            {ADMIN_NAV_ITEMS.map((item) => {
              const active = location.pathname === item.href ||
                (item.href !== '/dashboard' && location.pathname.startsWith(item.href))

              return (
                <li key={item.href}>
                  <Link
                    to={item.href}
                    aria-current={active ? 'page' : undefined}
                    title={sidebarCollapsed ? item.label : undefined}
                    className={`
                      flex items-center gap-3 px-3 py-2.5 rounded-sm
                      text-body-base font-semibold
                      transition-colors duration-100
                      focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-royal-orange
                      ${active
                        ? 'bg-royal-orange text-white'
                        : 'text-white/70 hover:bg-white/10 hover:text-white'
                      }
                      ${sidebarCollapsed ? 'justify-center' : ''}
                    `}
                  >
                    {item.icon}
                    {!sidebarCollapsed && <span>{item.label}</span>}
                  </Link>
                </li>
              )
            })}
          </ul>
        </nav>

        {/* User + logout */}
        <div className={`shrink-0 border-t border-white/10 p-3 ${sidebarCollapsed ? 'flex justify-center' : ''}`}>
          {!sidebarCollapsed && (
            <div className="mb-3">
              <p className="text-body-base font-semibold text-white truncate">{user?.full_name}</p>
              <p className="text-caption text-white/50 truncate">{user?.email}</p>
            </div>
          )}
          <button
            onClick={() => { clearAuth(); window.location.href = '/' }}
            title={sidebarCollapsed ? 'Sign out' : undefined}
            className={`
              flex items-center gap-2
              text-caption text-white/50 hover:text-error-red
              transition-colors duration-100
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-royal-orange rounded-sm
              ${sidebarCollapsed ? 'justify-center' : ''}
            `}
          >
            <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            {!sidebarCollapsed && <span>Sign Out</span>}
          </button>
        </div>
      </aside>

      {/* ── Main content area ─────────────────────────────────────────────── */}
      <div className={`flex-1 flex flex-col min-h-screen transition-all duration-200 ${sidebarCollapsed ? 'ml-16' : 'ml-60'}`}>
        {/* Admin top bar */}
        <header
          role="banner"
          aria-label="Admin header"
          className="sticky top-0 z-10 h-16 bg-white border-b border-mid-gray flex items-center px-6 shrink-0"
        >
          <div className="flex items-center gap-2">
            <p className="text-caption text-dark-gray">Admin Panel</p>
            <span className="text-mid-gray" aria-hidden="true">/</span>
            <p className="text-body-base font-semibold text-charcoal capitalize">
              {location.pathname.split('/').filter(Boolean).slice(1).join(' › ') || 'Dashboard'}
            </p>
          </div>

          <Link
            to="/"
            className="
              ml-auto text-body-base text-dark-gray hover:text-royal-orange
              transition-colors duration-150
              focus-visible:outline-none focus-visible:underline
            "
          >
            ← Back to Store
          </Link>
        </header>

        <main
          id="main-content"
          tabIndex={-1}
          className="flex-1 p-6 focus-visible:outline-none"
          aria-label="Admin main content"
        >
          <Outlet />
        </main>
      </div>
    </div>
  )
})